import React, { useEffect, useState } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useAuth } from 'hooks/AuthContext';
import { useAppState, useSelectedResume } from 'hooks/GlobalContext';
import { useNavigate, useParams } from 'react-router-dom';
import { Resume } from 'types/types';
import { ResumeEdit } from './ResumeEdit';
import { DesktopResumeDetails } from './ResumeViewerDetails';
import { ResumeMobileDialog } from './ResumeMobileDialog';
import { ResumeViewerTable } from './ResumeViewerTable';
import { SortField, SortOrder } from './resumeViewerTypes';
import { filterResumes, sortResumesList } from './resumeViewerUtils';

interface ResumeViewerProps {
  onSelect?: (resume: Resume) => void;
  candidateId?: string; // Optional filter by candidate
  jobId?: string; // Optional filter by job
}

const ResumeViewer: React.FC<ResumeViewerProps> = ({ onSelect, candidateId, jobId }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const { resumeId, mode = 'view' } = useParams<{ resumeId?: string; mode?: string }>();

  const { apiClient } = useAuth();
  const { selectedResume, setSelectedResume } = useSelectedResume(); // Assuming similar context
  const { setSnack } = useAppState();
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [sortField, setSortField] = useState<SortField>('updatedAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [mobileDialogOpen, setMobileDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredResumes, setFilteredResumes] = useState<Resume[]>([]);

  useEffect(() => {
    if (resumeId) {
      const resume = resumes.find(r => r.id === resumeId);
      if (resume) {
        setSelectedResume(resume);
        onSelect?.(resume);
        return;
      }
    }
  }, [resumes, resumeId, setSelectedResume, onSelect]);

  useEffect(() => {
    // Auto-select first resume if none selected
    if (filteredResumes.length > 0 && !selectedResume) {
      const firstResume = sortResumesList(filteredResumes, sortField, sortOrder)[0];
      setSelectedResume(firstResume);
      onSelect?.(firstResume);
    }
  }, [selectedResume, setSelectedResume, onSelect, filteredResumes, sortField, sortOrder]);

  useEffect(() => {
    if (resumes.length > 0) {
      return; // Avoid re-fetching if resumes are already loaded
    }

    const getResumes = async (): Promise<void> => {
      try {
        let result: Resume[];

        if (candidateId) {
          result = await apiClient.getResumesByCandidate(candidateId);
        } else if (jobId) {
          result = await apiClient.getResumesByJob(jobId);
        } else {
          result = await apiClient.getResumes();
        }

        setResumes(result);
        setFilteredResumes(result);
      } catch (err) {
        console.error('Failed to load resumes:', err);
        setSnack('Failed to load resumes: ' + err, 'error');
      }
    };

    getResumes();
  }, [
    apiClient,
    setSnack,
    candidateId,
    jobId,
    resumeId,
    selectedResume,
    setSelectedResume,
    onSelect,
    sortField,
    sortOrder,
    resumes.length,
  ]);

  // Filter resumes based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredResumes(resumes);
    } else {
      setFilteredResumes(filterResumes(resumes, searchQuery));
    }
  }, [searchQuery, resumes]);

  const handleSort = (field: SortField): void => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const handleResumeSelect = (resume: Resume): void => {
    setSelectedResume(resume);
    onSelect?.(resume);
    if (isMobile) {
      setMobileDialogOpen(true);
    } else {
      navigate(`/candidate/resumes/${resume.id}`);
    }
  };

  const handleMobileDialogClose = (): void => {
    setMobileDialogOpen(false);
  };

  const sortedResumes = sortResumesList(filteredResumes, sortField, sortOrder);

  const getDisplayTitle = (): string => {
    if (candidateId) return `Resumes for Candidate`;
    if (jobId) return `Resumes for Job`;
    return `All Resumes`;
  };

  const resumeList = (
    <ResumeViewerTable
      resumes={sortedResumes}
      selectedResume={selectedResume}
      sortField={sortField}
      sortOrder={sortOrder}
      searchQuery={searchQuery}
      title={getDisplayTitle()}
      isMobile={isMobile}
      isSmall={isSmall}
      onSelect={handleResumeSelect}
      onSort={handleSort}
      onSortSelect={(field, order) => {
        setSortField(field);
        setSortOrder(order);
      }}
      onSearchChange={setSearchQuery}
    />
  );

  const handleSave = (updatedResume: Resume): void => {
    setSelectedResume(updatedResume);
  };

  if (mode === 'edit' && selectedResume) {
    return (
      <ResumeEdit
        // onClose={() => {
        //   navigate(window.location.pathname.replace('/edit', ''));
        // }}
        resumeId={selectedResume.id}
        onSave={handleSave}
      />
    );
  }

  if (isMobile) {
    return (
      <Box
        sx={{
          height: '100%',
          p: 0.5,
          backgroundColor: 'background.default',
        }}
      >
        {resumeList}

        <ResumeMobileDialog
          open={mobileDialogOpen}
          selectedResume={selectedResume}
          onClose={handleMobileDialogClose}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        gap: 0.75,
        p: 0.75,
        backgroundColor: 'background.default',
      }}
    >
      {resumeList}
      <DesktopResumeDetails selectedResume={selectedResume} />
    </Box>
  );
};

export { ResumeViewer };
